<script setup lang="ts">
  import SearchIcon from '../Icons/SearchIcon.vue'

  export interface SearchSuggestionItemProps {
    text: string
    description?: string
    image?: string
    query?: string
    active?: boolean
    searchboxVariant?: 'default' | 'v2' | 'v2-big-yolo' | 'v3'
  }

  const props = withDefaults(defineProps<SearchSuggestionItemProps>(), {
    description: '',
    image: '',
    query: '',
    active: false,
    searchboxVariant: 'default',
  })

  const intialQuery = props.query
  const matchingQuery = ref('')
  const nonMatchingQuery = ref('')

  const type = computed(() => (props.image ? 'item-image' : 'default-icon'))

  const fontWeight = computed(() => (props.image ? 'font-bold' : 'font-normal'))

  const findMatchInSuggestions = () => {
    if (intialQuery !== '' && props.query !== '' && !props.image) {
      const text = props.text
      const initialLower = intialQuery.toLowerCase()
      const index = text.toLowerCase().indexOf(initialLower)
      if (index === 0) {
        matchingQuery.value = text.substring(0, initialLower.length)
        nonMatchingQuery.value = text.substring(initialLower.length)
      } else {
        matchingQuery.value = ''
        nonMatchingQuery.value = ''
      }
    }
  }
  findMatchInSuggestions()
</script>
<template>
  <li
    :class="[
      {
        'gap-4 px-6 py-2': type === 'default-icon',
        'py-3': type === 'default-icon' && searchboxVariant === 'v2-big-yolo',
        'gap-2 py-2 pl-2 pr-6': type === 'item-image',
      },
      active
        ? 'bg-action-neutral-subtle-hover'
        : 'bg-background-default-strong',
    ]"
    class="flex items-center hover:cursor-pointer"
    data-testid="search-suggestion"
  >
    <NuxtImg
      v-if="image"
      :src="image"
      :class="[`search__image--${type}`]"
      class="h-[40px] w-[40px] rounded object-contain"
    />
    <SearchIcon v-else :size="searchboxVariant === 'v2-big-yolo' ? 24 : 16" />
    <div
      :class="[
        {
          block: type === 'default-icon',
          'flex flex-col justify-center': type === 'item-image',
        },
      ]"
    >
      <span v-if="!intialQuery || nonMatchingQuery === ''">
        <Typography
          color="text-content-primary"
          :weight="fontWeight"
          :variant="
            searchboxVariant === 'v2-big-yolo' ? 'text-xl' : 'text-base'
          "
        >
          {{ text }}
        </Typography>
      </span>
      <span v-else class="flex">
        <Typography
          :variant="
            searchboxVariant === 'v2-big-yolo' ? 'text-xl' : 'text-base'
          "
          color="text-content-primary"
        >
          {{ matchingQuery }}
        </Typography>
        <Typography
          :variant="
            searchboxVariant === 'v2-big-yolo' ? 'text-xl' : 'text-base'
          "
          color="text-content-primary"
          weight="font-bold"
          class="whitespace-pre"
        >
          {{ nonMatchingQuery }}
        </Typography>
      </span>

      <Typography
        v-if="description"
        variant="text-xs"
        color="text-content-secondary"
      >
        {{ description }}
      </Typography>
    </div>
  </li>
</template>
<style scoped>
  :deep(p) {
    margin-bottom: 0;
  }
</style>
